import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { MapView } from '../components/MapView';
import { Button, Card, Badge, Alert } from '../components/design-system';
import { Input, TextArea, Select } from '../components/design-system';
import {
  Plus, Users, Package, Box, Clock, X,
  MapPin, Navigation, ChevronDown, DollarSign,
  Star, ChevronRight, Check, XCircle, Truck,
} from 'lucide-react';
import courierApi from '../services/courierApi';
import { useGeolocation } from '../hooks/useGeolocation';
import { useGeocode } from '../hooks/useGeocode';

export const HomePage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { position } = useGeolocation();
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestType, setRequestType] = useState('parcel');
  const [activeContracts, setActiveContracts] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [sheetState, setSheetState] = useState('default');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestOffers, setRequestOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [contractsRes, requestsRes] = await Promise.allSettled([
        courierApi.listContracts(),
        courierApi.listRequests({ role: 'customer' }),
      ]);
      if (contractsRes.status === 'fulfilled') setActiveContracts(contractsRes.value.contracts || []);
      if (requestsRes.status === 'fulfilled') setMyRequests(requestsRes.value.requests || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  const viewOffers = async (req) => {
    setSelectedRequest(req);
    setOffersLoading(true);
    setSheetState('expanded');
    try {
      const res = await courierApi.listOffers({ requestId: req.id });
      setRequestOffers(res.offers || []);
    } catch (err) {
      console.error('Failed to load offers:', err);
    } finally {
      setOffersLoading(false);
    }
  };

  const handleAcceptOffer = async (offerId) => {
    setActionLoading(true);
    try {
      await courierApi.acceptOffer(offerId);
      setSelectedRequest(null);
      setRequestOffers([]);
      setSheetState('default');
      await loadData();
    } catch (err) {
      console.error('Failed to accept offer:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectOffer = async (offerId) => {
    setActionLoading(true);
    try {
      await courierApi.rejectOffer(offerId);
      const res = await courierApi.listOffers({ requestId: selectedRequest.id });
      setRequestOffers(res.offers || []);
      if ((res.offers || []).filter(o => o.status === 'pending').length === 0) {
        setSelectedRequest(null);
        setSheetState('default');
      }
      await loadData();
    } catch (err) {
      console.error('Failed to reject offer:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const openRequestForm = (type) => {
    setRequestType(type);
    setSheetState('collapsed');
    setShowRequestForm(true);
  };

  const closeRequestForm = () => {
    setShowRequestForm(false);
    setSheetState('default');
  };

  // Build map markers from in-progress contracts
  const inProgressStatuses = ['active', 'pickup_enroute', 'picked_up', 'in_transit', 'delivered'];
  const contractMapMarkers = activeContracts
    .filter(c => inProgressStatuses.includes(c.status))
    .flatMap(c => {
      const markers = [];
      if (c.pickupLatitude != null && c.pickupLongitude != null) {
        markers.push({ latitude: c.pickupLatitude, longitude: c.pickupLongitude, type: 'pickup', label: `📦 ${c.requestTitle} — ${t('map.pickup')}` });
      }
      if (c.dropoffLatitude != null && c.dropoffLongitude != null) {
        markers.push({ latitude: c.dropoffLatitude, longitude: c.dropoffLongitude, type: 'dropoff', label: `📍 ${c.requestTitle} — ${t('map.dropoff')}` });
      }
      return markers;
    });

  const transportTypes = [
    { icon: Users, label: t('request.types.persons'), value: 'persons', color: 'bg-blue-100 text-blue-600' },
    { icon: Package, label: t('request.types.parcel'), value: 'parcel', color: 'bg-orange-100 text-orange-600' },
    { icon: Box, label: t('request.types.large_objects'), value: 'large_objects', color: 'bg-purple-100 text-purple-600' },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-7.5rem)]">
      {/* Map area — form overlays inside this container only */}
      <div className="flex-1 relative overflow-hidden">
        <MapView className="w-full h-full" markers={contractMapMarkers} />

        {/* Floating greeting — hidden when form is open */}
        {!showRequestForm && (
          <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2 shadow-md z-[1000]">
            <p className="text-sm font-medium text-gray-900">
              {t('home.greeting', { name: user?.firstName || 'User' })}
            </p>
            <p className="text-xs text-gray-500">{t('home.findTransport')}</p>
          </div>
        )}

        {/* Create Request form — overlays the map area only */}
        {showRequestForm && (
          <>
            {/* Backdrop — scoped to map container */}
            <div
              className="absolute inset-0 bg-black/40 z-[1001] animate-fade-in"
              onClick={closeRequestForm}
            />
            {/* Form panel — slides up from bottom of map area */}
            <div className="absolute inset-x-2 bottom-2 z-[1002] bg-white rounded-2xl shadow-xl animate-slide-up max-h-[calc(100%-1rem)] flex flex-col">
              {/* Handle + header */}
              <div className="flex-shrink-0">
                <div className="flex justify-center pt-2 pb-1">
                  <div className="w-10 h-1 bg-gray-300 rounded-full" />
                </div>
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
                  <h3 className="text-base font-semibold text-gray-900">{t('request.create')}</h3>
                  <button
                    onClick={closeRequestForm}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
              </div>
              {/* Scrollable form content */}
              <div className="flex-1 overflow-y-auto px-4 py-3">
                <CreateRequestForm
                  initialType={requestType}
                  position={position}
                  onCreated={() => {
                    closeRequestForm();
                    loadData();
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bottom drawer — always visible, never dimmed */}
      <div className={`bg-white border-t border-gray-200 transition-all duration-300 overflow-hidden flex-shrink-0 ${
        sheetState === 'expanded' ? 'max-h-[60vh]' : sheetState === 'collapsed' ? 'max-h-[36px]' : 'max-h-[280px]'
      }`}>
        {/* Handle */}
        <button
          onClick={() => {
            if (showRequestForm) return;
            if (sheetState === 'collapsed') setSheetState('default');
            else if (sheetState === 'default') setSheetState('expanded');
            else setSheetState('collapsed');
          }}
          className="w-full flex justify-center pt-2 pb-1"
        >
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </button>

        <div className="px-4 pb-4 overflow-y-auto scrollbar-hide" style={{ maxHeight: sheetState === 'expanded' ? 'calc(60vh - 36px)' : '244px' }}>
          {/* Transport type selector */}
          <div className="mb-3">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">{t('home.needTransport')}</p>
            <div className="grid grid-cols-3 gap-2">
              {transportTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => openRequestForm(type.value)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all"
                >
                  <div className={`h-10 w-10 rounded-full ${type.color} flex items-center justify-center`}>
                    <type.icon className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-medium text-gray-700">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Active contracts summary */}
          {activeContracts.filter(c => inProgressStatuses.includes(c.status)).length > 0 && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-500 uppercase">{t('home.activeContracts')}</p>
                <button onClick={() => navigate('/contracts')} className="text-xs text-primary-600 font-medium">
                  {t('home.viewAll')}
                </button>
              </div>
              {activeContracts.filter(c => inProgressStatuses.includes(c.status)).slice(0, 3).map((contract) => {
                const statusVariant = { active: 'primary', pickup_enroute: 'info', picked_up: 'info', in_transit: 'warning', delivered: 'success' }[contract.status] || 'default';
                const needsConfirmation = contract.status === 'delivered';
                return (
                  <button
                    key={contract.id}
                    onClick={() => navigate(`/contracts/${contract.id}`)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border mb-2 transition-colors ${
                      needsConfirmation ? 'border-green-300 bg-green-50/50 hover:bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      needsConfirmation ? 'bg-green-100' : 'bg-primary-100'
                    }`}>
                      {needsConfirmation ? <Check className="h-5 w-5 text-green-600" /> : <Truck className="h-5 w-5 text-primary-600" />}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{contract.requestTitle}</p>
                      <p className="text-xs text-gray-500">
                        {contract.providerName}
                        {needsConfirmation && <span className="text-green-600 font-medium"> — {t('contract.needsConfirmation')}</span>}
                      </p>
                    </div>
                    <Badge variant={statusVariant} size="sm">
                      {t(`contract.status.${contract.status}`)}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}

          {/* My requests */}
          {myRequests.filter(r => ['open', 'matched'].includes(r.status)).length > 0 && !selectedRequest && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-500 uppercase">{t('request.myRequests')}</p>
              </div>
              {myRequests.filter(r => ['open', 'matched'].includes(r.status)).slice(0, 4).map((req) => {
                const isMatched = req.status === 'matched';
                const statusVariant = isMatched ? 'success' : 'warning';
                const bgColor = isMatched ? 'bg-green-100' : 'bg-yellow-100';
                const iconColor = isMatched ? 'text-green-600' : 'text-yellow-600';
                return (
                  <button
                    key={req.id}
                    onClick={() => isMatched ? viewOffers(req) : null}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border mb-2 transition-colors text-left ${
                      isMatched ? 'border-green-300 bg-green-50/50 hover:bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <div className={`h-10 w-10 rounded-full ${bgColor} flex items-center justify-center flex-shrink-0`}>
                      {isMatched ? <Star className="h-5 w-5 text-green-600" /> : <Clock className={`h-5 w-5 ${iconColor}`} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{req.title}</p>
                      <p className="text-xs text-gray-500">
                        {t(`request.types.${req.type}`)}
                        {isMatched && <span className="text-green-600 font-medium"> — {t('offer.offersReceived')}</span>}
                      </p>
                    </div>
                    {isMatched ? (
                      <ChevronRight className="h-5 w-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <Badge variant={statusVariant} size="sm">
                        {t(`request.status.${req.status}`)}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Offer review panel */}
          {selectedRequest && (
            <div>
              <button
                onClick={() => { setSelectedRequest(null); setRequestOffers([]); setSheetState('default'); }}
                className="flex items-center gap-1 text-xs text-gray-500 mb-2 hover:text-gray-700"
              >
                <ChevronDown className="h-3 w-3 rotate-90" />
                {t('common.back')}
              </button>
              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <p className="text-sm font-semibold text-gray-900">{selectedRequest.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t(`request.types.${selectedRequest.type}`)}</p>
              </div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">{t('offer.offersReceived')}</p>
              {offersLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" />
                </div>
              ) : requestOffers.filter(o => o.status === 'pending').length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">{t('offer.noOffers')}</p>
              ) : (
                <div className="space-y-2">
                  {requestOffers.filter(o => o.status === 'pending').map((offer) => (
                    <div key={offer.id} className="border border-gray-200 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{offer.providerName || t('contract.provider')}</p>
                          {offer.vehicleType && (
                            <p className="text-xs text-gray-500">{offer.vehicleType}{offer.ratingAvg ? ` — ★ ${offer.ratingAvg.toFixed(1)}` : ''}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-base font-bold text-gray-900">{offer.feeAmount} {offer.feeCurrency}</p>
                          <p className="text-xs text-gray-500">{t('offer.proposedFee')}</p>
                        </div>
                      </div>
                      {offer.estimatedPickupTime && (
                        <p className="text-xs text-gray-500 mb-1">
                          <Clock className="h-3 w-3 inline mr-1" />
                          {t('offer.estimatedPickup')}: {new Date(offer.estimatedPickupTime).toLocaleString()}
                        </p>
                      )}
                      {offer.termsText && (
                        <p className="text-xs text-gray-500 mb-2 italic">{offer.termsText}</p>
                      )}
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          fullWidth
                          onClick={() => handleAcceptOffer(offer.id)}
                          loading={actionLoading}
                        >
                          <Check className="h-4 w-4" />
                          {t('offer.accept')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          fullWidth
                          onClick={() => handleRejectOffer(offer.id)}
                          loading={actionLoading}
                        >
                          <XCircle className="h-4 w-4" />
                          {t('offer.reject')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeContracts.length === 0 && myRequests.length === 0 && (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500">{t('home.noActiveContracts')}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => openRequestForm('parcel')}
              >
                <Plus className="h-4 w-4" />
                {t('request.create')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AddressInput = ({ label, placeholder, value, onChange, onSelect, icon }) => {
  const { suggestions, loading: geoLoading, search, clear } = useGeocode();
  const [focused, setFocused] = useState(false);

  return (
    <div className="relative">
      <Input
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          search(e.target.value);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 200)}
        leftIcon={icon}
        required
        fullWidth
      />
      {focused && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-primary-50 border-b border-gray-100 last:border-0"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect(s);
                clear();
              }}
            >
              <MapPin className="h-3 w-3 inline mr-1.5 text-gray-400" />
              {s.displayName}
            </button>
          ))}
        </div>
      )}
      {geoLoading && (
        <div className="absolute right-3 top-9">
          <div className="animate-spin h-3 w-3 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      )}
    </div>
  );
};

const CreateRequestForm = ({ initialType, position, onCreated }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    type: initialType || 'parcel',
    title: '',
    description: '',
    pickupAddress: '',
    pickupLatitude: 0,
    pickupLongitude: 0,
    dropoffAddress: '',
    dropoffLatitude: 0,
    dropoffLongitude: 0,
    urgency: 'normal',
    scheduledTime: '',
    passengerCount: 1,
    estimatedWeightKg: 0,
    specialRequirements: '',
    budgetMin: '',
    budgetMax: '',
    budgetCurrency: 'EUR',
  });

  useEffect(() => {
    if (initialType && initialType !== form.type) {
      setForm(f => ({ ...f, type: initialType }));
    }
  }, [initialType]);

  useEffect(() => {
    if (position) {
      setForm(f => ({
        ...f,
        pickupLatitude: position.latitude,
        pickupLongitude: position.longitude,
      }));
    }
  }, [position]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await courierApi.createTransportRequest({
        ...form,
        scheduledTime: form.urgency === 'asap' ? null : form.scheduledTime || null,
        budgetMin: form.budgetMin ? parseFloat(form.budgetMin) : null,
        budgetMax: form.budgetMax ? parseFloat(form.budgetMax) : null,
        budgetCurrency: form.budgetCurrency,
      });
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <>
      {error && (
        <Alert variant="error" className="mb-4" dismissible onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 pb-2">
        <Select
          label={t('request.type')}
          value={form.type}
          onChange={update('type')}
          options={[
            { value: 'persons', label: t('request.types.persons') },
            { value: 'parcel', label: t('request.types.parcel') },
            { value: 'large_objects', label: t('request.types.large_objects') },
          ]}
          fullWidth
        />

        <Input
          label={t('request.title')}
          placeholder={t('request.titlePlaceholder')}
          value={form.title}
          onChange={update('title')}
          required
          fullWidth
        />

        <TextArea
          label={t('request.description')}
          placeholder={t('request.descriptionPlaceholder')}
          value={form.description}
          onChange={update('description')}
          rows={2}
          fullWidth
        />

        <AddressInput
          label={t('request.pickup')}
          placeholder={t('request.pickupPlaceholder')}
          value={form.pickupAddress}
          onChange={(val) => setForm(f => ({ ...f, pickupAddress: val }))}
          onSelect={(s) => setForm(f => ({ ...f, pickupAddress: s.displayName, pickupLatitude: s.latitude, pickupLongitude: s.longitude }))}
          icon={<MapPin className="h-4 w-4 text-green-500" />}
        />

        <AddressInput
          label={t('request.dropoff')}
          placeholder={t('request.dropoffPlaceholder')}
          value={form.dropoffAddress}
          onChange={(val) => setForm(f => ({ ...f, dropoffAddress: val }))}
          onSelect={(s) => setForm(f => ({ ...f, dropoffAddress: s.displayName, dropoffLatitude: s.latitude, dropoffLongitude: s.longitude }))}
          icon={<MapPin className="h-4 w-4 text-red-500" />}
        />

        <Select
          label={t('request.urgency')}
          value={form.urgency}
          onChange={update('urgency')}
          options={[
            { value: 'asap', label: t('request.urgencyOptions.asap') },
            { value: 'normal', label: t('request.urgencyOptions.normal') },
            { value: 'flexible', label: t('request.urgencyOptions.flexible') },
          ]}
          fullWidth
        />

        {form.urgency !== 'asap' && (
          <Input
            label={t('request.timing')}
            type="datetime-local"
            value={form.scheduledTime}
            onChange={update('scheduledTime')}
            fullWidth
          />
        )}

        {form.type === 'persons' && (
          <Input
            label={t('request.passengers')}
            type="number"
            min="1"
            value={form.passengerCount}
            onChange={(e) => setForm({ ...form, passengerCount: parseInt(e.target.value) || 1 })}
            fullWidth
          />
        )}

        {form.type !== 'persons' && (
          <Input
            label={t('request.weight')}
            type="number"
            min="0"
            step="0.1"
            value={form.estimatedWeightKg}
            onChange={(e) => setForm({ ...form, estimatedWeightKg: parseFloat(e.target.value) || 0 })}
            fullWidth
          />
        )}

        <TextArea
          label={t('request.specialRequirements')}
          placeholder={t('request.specialRequirementsPlaceholder')}
          value={form.specialRequirements}
          onChange={update('specialRequirements')}
          rows={2}
          fullWidth
        />

        {/* Optional budget expectation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('request.budget')} <span className="text-xs text-gray-400 font-normal">({t('request.budgetOptional')})</span>
          </label>
          <div className="grid grid-cols-5 gap-2">
            <div className="col-span-2">
              <Input
                placeholder={t('request.budgetMinPlaceholder')}
                type="number"
                min="0"
                step="0.01"
                value={form.budgetMin}
                onChange={(e) => setForm({ ...form, budgetMin: e.target.value })}
                leftIcon={<DollarSign className="h-4 w-4 text-gray-400" />}
                fullWidth
              />
            </div>
            <div className="col-span-2">
              <Input
                placeholder={t('request.budgetMaxPlaceholder')}
                type="number"
                min="0"
                step="0.01"
                value={form.budgetMax}
                onChange={(e) => setForm({ ...form, budgetMax: e.target.value })}
                leftIcon={<DollarSign className="h-4 w-4 text-gray-400" />}
                fullWidth
              />
            </div>
            <Select
              value={form.budgetCurrency}
              onChange={(e) => setForm({ ...form, budgetCurrency: e.target.value })}
              options={[
                { value: 'EUR', label: '€' },
                { value: 'USD', label: '$' },
                { value: 'GBP', label: '£' },
                { value: 'SEK', label: 'kr' },
              ]}
              fullWidth
            />
          </div>
          <p className="text-xs text-gray-400 mt-1">{t('request.budgetHint')}</p>
        </div>

        <Button type="submit" fullWidth loading={loading} size="lg">
          {t('request.submitRequest')}
        </Button>
      </form>
    </>
  );
};
